import type { StatusTone } from '../components/ui';
import { APP_ROLLEN, ERP_LESERECHTE, appRolleVon, type AppRolle, type Recht } from '../berechtigungen';

export interface RollenAnzeige {
  label: string;
  tone: StatusTone;
  /** Ein Satz, was die Rolle darf -- fuer Profil, Benutzerliste und "Kein Zugriff". */
  hinweis: string;
}

/**
 * Deutsche Anzeige der fuenf Anwendungsrollen (Verbesserungsplan, Paket 2
 * Schritt 5). Die Rolle selbst kommt als `appRolle` aus GET /api/auth/me;
 * Kopfzeile, Profil und Benutzerliste zeigen nur noch dieses Label, nicht
 * mehr `member`/`owner`.
 */
export const APP_ROLLEN_LABEL: Record<AppRolle, RollenAnzeige> = {
  mitarbeit: {
    label: 'Mitarbeit',
    tone: 'neutral',
    hinweis: 'Pflegt Inhalte und Vertriebsdaten, stellt keine Belege aus und versendet nichts nach außen.',
  },
  marketing: {
    label: 'Marketing',
    tone: 'info',
    hinweis: 'Wie Mitarbeit, zusätzlich LinkedIn-Beiträge freigeben und veröffentlichen.',
  },
  vertrieb: {
    label: 'Vertrieb',
    tone: 'primary',
    hinweis: 'Angebote, Versand, Löschen und Zusammenführen im Vertrieb; Aufträge und Lager nur lesend, offene Posten je Firma als Summe; keine Rechnungen, Zahlungen, Einkauf oder Buchhaltung.',
  },
  buchhaltung: {
    label: 'Buchhaltung',
    tone: 'warning',
    hinweis: 'ERP und Buchhaltung vollständig mit DATEV-Export; Vertrieb nur lesend, keine Verwaltung.',
  },
  inhaber: {
    label: 'Inhaber:in',
    tone: 'success',
    hinweis: 'Alles, einschließlich Verwaltung, Optionen, Festschreibung und Kontenrahmen.',
  },
};

/** Die Felder der angemeldeten Person, aus denen sich die angezeigte Rolle ergibt. */
export interface RollenQuelle {
  appRolle?: string | null;
  role?: string | null;
  tenantRole?: string | null;
}

/**
 * Angezeigte App-Rolle. Spiegelt appRolleVon() im Backend: Plattform-Admin
 * (`role` admin) und Mandanten-Inhaber:in (`tenantRole` owner) sind immer
 * `inhaber`, auch wenn die Spalte `appRolle` noch den Standard `mitarbeit`
 * traegt (Konten aus der Zeit vor Paket 1 im Entwicklungsmodus; im
 * Keycloak-Betrieb schreibt die Anmeldung die Spalte bei jedem Login).
 * GET /api/auth/me liefert die Spalte roh, die `rechte` aber aufgeloest --
 * ohne diese Regel stuende "Mitarbeit" neben Inhaber-Rechten.
 */
export function angezeigteAppRolle(user: RollenQuelle | null | undefined): AppRolle {
  if (user && (user.role === 'admin' || user.tenantRole === 'owner')) return 'inhaber';
  return appRolleVon(user);
}

/** Label, Farbe und Hinweis zur angezeigten App-Rolle; unbekannt oder fehlend zaehlt als Mitarbeit (wie im Backend). */
export const appRolleLabel = (user: RollenQuelle | null | undefined): RollenAnzeige => APP_ROLLEN_LABEL[angezeigteAppRolle(user)];

/*
 * Welche Rolle hat welches Recht: Kopie von MATRIX in
 * intermal/src/middleware/berechtigungen.ts (Anhang B des Plans). Die
 * Oberflaeche entscheidet damit nichts; sie beantwortet nur die Frage
 * "wem ist diese Seite vorbehalten?" auf der Seite "Kein Zugriff" und
 * baut Testbenutzer je Rolle. Massgeblich bleiben die `rechte` aus
 * GET /api/auth/me. Wer die Matrix im Backend aendert, aendert sie hier mit.
 */
const MITARBEIT: readonly Recht[] = [
  'linkedin.lesen', 'linkedin.bearbeiten',
  'inhalte.lesen', 'inhalte.bearbeiten',
  'vertrieb.lesen', 'vertrieb.bearbeiten',
  'angebot.lesen', 'versand.lesen',
  'projekt.lesen', 'zeit.eigene',
];
const MARKETING: readonly Recht[] = [...MITARBEIT, 'linkedin.freigeben'];
const VERTRIEB: readonly Recht[] = [
  ...MITARBEIT,
  'linkedin.freigeben', 'vertrieb.loeschen',
  'angebot.bearbeiten', 'angebot.versenden',
  'versand.ausfuehren',
  'erp.auftrag.lesen', 'erp.lager.lesen', 'erp.offene_posten.lesen',
];
const BUCHHALTUNG: readonly Recht[] = [
  'vertrieb.lesen', 'angebot.lesen',
  ...ERP_LESERECHTE, 'erp.bearbeiten', 'erp.ausstellen',
  'buchhaltung.lesen', 'buchhaltung.buchen', 'buchhaltung.datev',
  'projekt.lesen', 'zeit.eigene', 'projekt.verwalten', 'zeit.fremde',
  'erp.einstellungen.lesen',
];
const INHABER: readonly Recht[] = [
  'linkedin.lesen', 'linkedin.bearbeiten', 'linkedin.freigeben',
  'inhalte.lesen', 'inhalte.bearbeiten',
  'vertrieb.lesen', 'vertrieb.bearbeiten', 'vertrieb.loeschen',
  'angebot.lesen', 'angebot.bearbeiten', 'angebot.versenden',
  'versand.lesen', 'versand.ausfuehren',
  ...ERP_LESERECHTE, 'erp.bearbeiten', 'erp.ausstellen',
  'buchhaltung.lesen', 'buchhaltung.buchen',
  'buchhaltung.festschreiben', 'buchhaltung.datev', 'buchhaltung.konten',
  'projekt.lesen', 'zeit.eigene', 'projekt.verwalten', 'zeit.fremde',
  'erp.einstellungen.lesen', 'erp.einstellungen.bearbeiten',
  'admin.optionen', 'admin.benutzer', 'admin.mandant',
];

export const RECHTE_JE_ROLLE: Record<AppRolle, readonly Recht[]> = {
  mitarbeit: MITARBEIT,
  marketing: MARKETING,
  vertrieb: VERTRIEB,
  buchhaltung: BUCHHALTUNG,
  inhaber: INHABER,
};

/**
 * Deutsche Bezeichnung je Recht, nach Bereich gruppiert -- fuer den Dialog
 * "Rechte anpassen" (Rechte je Person, Entscheidung 17) in der
 * Benutzerverwaltung. `admin.*` steht mit drin, laesst sich dort aber nicht
 * erteilen (nur Rolle/Keycloak, siehe istPersonRecht im Backend).
 */
export const RECHT_GRUPPEN: ReadonlyArray<{ label: string; rechte: ReadonlyArray<{ recht: Recht; label: string }> }> = [
  { label: 'LinkedIn-Studio', rechte: [
    { recht: 'linkedin.lesen', label: 'Lesen' },
    { recht: 'linkedin.bearbeiten', label: 'Bearbeiten' },
    { recht: 'linkedin.freigeben', label: 'Beiträge freigeben und veröffentlichen' },
  ] },
  { label: 'Inhalte & Werkzeuge', rechte: [
    { recht: 'inhalte.lesen', label: 'Lesen' },
    { recht: 'inhalte.bearbeiten', label: 'Bearbeiten' },
  ] },
  { label: 'Vertrieb', rechte: [
    { recht: 'vertrieb.lesen', label: 'Lesen' },
    { recht: 'vertrieb.bearbeiten', label: 'Bearbeiten' },
    { recht: 'vertrieb.loeschen', label: 'Löschen und Zusammenführen' },
  ] },
  { label: 'Angebote', rechte: [
    { recht: 'angebot.lesen', label: 'Lesen' },
    { recht: 'angebot.bearbeiten', label: 'Bearbeiten' },
    { recht: 'angebot.versenden', label: 'Versenden, Auftrag aus Angebot' },
  ] },
  { label: 'Versand', rechte: [
    { recht: 'versand.lesen', label: 'Lesen' },
    { recht: 'versand.ausfuehren', label: 'Ausführen' },
  ] },
  { label: 'ERP', rechte: [
    { recht: 'erp.auftrag.lesen', label: 'Aufträge lesen' },
    { recht: 'erp.lager.lesen', label: 'Artikel & Lager lesen' },
    { recht: 'erp.offene_posten.lesen', label: 'Offene Posten je Firma (nur Summe, Firmenakte)' },
    { recht: 'erp.rechnung.lesen', label: 'Rechnungen und Tagesabrechnung lesen' },
    { recht: 'erp.zahlung.lesen', label: 'Zahlungen, offene Posten und Mahnwesen lesen' },
    { recht: 'erp.einkauf.lesen', label: 'Einkauf lesen (Lieferanten, Bestellungen, Eingangsrechnungen)' },
    { recht: 'erp.bearbeiten', label: 'Belege bearbeiten (Entwürfe)' },
    { recht: 'erp.ausstellen', label: 'Ausstellen, bestätigen, Zahlungen buchen, mahnen' },
  ] },
  { label: 'Buchhaltung', rechte: [
    { recht: 'buchhaltung.lesen', label: 'Lesen (Journal, Konten, UStVA)' },
    { recht: 'buchhaltung.buchen', label: 'Buchen' },
    { recht: 'buchhaltung.festschreiben', label: 'Festschreiben' },
    { recht: 'buchhaltung.datev', label: 'DATEV-Export' },
    { recht: 'buchhaltung.konten', label: 'Kontenrahmen pflegen' },
  ] },
  { label: 'Projekte & Zeiten', rechte: [
    { recht: 'projekt.lesen', label: 'Projekte lesen' },
    { recht: 'zeit.eigene', label: 'Eigene Zeiten buchen' },
    { recht: 'projekt.verwalten', label: 'Projekte verwalten' },
    { recht: 'zeit.fremde', label: 'Zeiten anderer buchen' },
  ] },
  { label: 'ERP-Einstellungen', rechte: [
    { recht: 'erp.einstellungen.lesen', label: 'Lesen' },
    { recht: 'erp.einstellungen.bearbeiten', label: 'Bearbeiten' },
  ] },
  { label: 'Verwaltung (nur über die Rolle)', rechte: [
    { recht: 'admin.optionen', label: 'Optionen' },
    { recht: 'admin.benutzer', label: 'Benutzer' },
    { recht: 'admin.mandant', label: 'Mandant' },
  ] },
];

/** Darf das Recht je Person erteilt werden? Spiegel von istPersonRecht im Backend: alles ausser admin.*. */
export const istPersonRecht = (recht: Recht): boolean => !recht.startsWith('admin.');

/** Rollen, die das Recht haben, in der Reihenfolge von APP_ROLLEN. */
export function rollenMitRecht(recht: Recht): AppRolle[] {
  return APP_ROLLEN.filter((rolle) => RECHTE_JE_ROLLE[rolle].includes(recht));
}

/**
 * "der Rolle Inhaber:in" bzw. "den Rollen Vertrieb, Buchhaltung und
 * Inhaber:in" -- fuer den Satz "Diese Seite ist ... vorbehalten". Ohne
 * Rolle (Recht nur fuer Plattform-Admins) nennt der Satz die Administration.
 */
export function rollenAufzaehlung(rollen: readonly AppRolle[]): string {
  const labels = rollen.map((rolle) => APP_ROLLEN_LABEL[rolle].label);
  if (labels.length === 0) return 'der Plattform-Administration';
  if (labels.length === 1) return `der Rolle ${labels[0]}`;
  return `den Rollen ${labels.slice(0, -1).join(', ')} und ${labels[labels.length - 1]}`;
}

/* --- Mandanten- und Systemrollen (Benutzerverwaltung, Entwicklungsmodus) --- */

export const MANDANTEN_ROLLEN: Record<string, { label: string; tone: StatusTone; hinweis: string }> = {
  owner: { label: 'Eigentümer', tone: 'success', hinweis: 'Alles, einschließlich Rollen vergeben und Mandant bearbeiten' },
  admin: { label: 'Administrator', tone: 'info', hinweis: 'Benutzer einladen und entfernen' },
  member: { label: 'Mitglied', tone: 'neutral', hinweis: 'Arbeitet mit den Inhalten, verwaltet nichts' },
};

export const SYSTEM_ROLLEN: Record<string, string> = { user: 'Benutzer', admin: 'Admin' };

export const mandantenRolle = (r?: string | null) =>
  MANDANTEN_ROLLEN[r ?? ''] ?? { label: r || 'Unbekannt', tone: 'neutral' as StatusTone, hinweis: '' };

export const systemRolle = (r?: string | null) => SYSTEM_ROLLEN[r ?? ''] ?? (r || '—');

export const initialen = (name?: string | null) =>
  (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((t) => t[0]!.toUpperCase()).join('') || '?';
